/**
 * Photo URLs and photo bytes, in one place.
 *
 * CSA keys its own S3 objects `csa/{audit_id}/{local_id}.jpg`, with the small
 * variant alongside as `{local_id}_t.jpg`, so the thumbnail can be derived
 * rather than carried twice in the report model.
 *
 * Using the thumbnail is not a nicety. The export spike measured **178 MB
 * against 28 MB** for this choice (§7a) — a report that embeds full-size
 * 1600px photographs is one nobody can email.
 *
 * `thumbFor` lived as two identical copies, one in each PDF renderer. They
 * agreed only by luck: a fix applied to one would have left the other
 * embedding originals, and the symptom — a report that is simply large — is
 * not one anybody reports as a bug.
 */

/*
 * Objects uploaded before the module was renamed are keyed `crsi/…`, and the
 * rename deliberately did not move them: rewriting live S3 keys to tidy a
 * prefix risks losing the evidence attached to an issued report, for no gain a
 * reader can see.
 *
 * So both prefixes are matched. Without the `crsi` branch an older photo falls
 * through and the full-size original is embedded instead — the exact failure
 * the thumbnail exists to prevent, on precisely the reports most likely to
 * have been issued to a client already.
 */
const KEYED = /\/(csa|crsi)\/\d+\/[^/]+\.jpg$/i;

/** The small variant, where one can be derived. Anything else is returned as-is. */
export function thumbFor(url) {
  if (typeof url !== "string") return url;
  return KEYED.test(url) ? url.replace(/\.jpg$/i, "_t.jpg") : url;
}

/** Exported for the tests, so the prefixes cannot be changed unnoticed. */
export const THUMBNAIL_KEY_PATTERN = KEYED;

/*
 * The audit and photo this URL belongs to, pulled back out of the S3 key.
 *
 * The report model carries URLs, not ids — that is what the renderers need —
 * so serving the bytes through our own API means recovering the two parts the
 * route is addressed by. A URL that does not match this shape is somebody
 * else's, and is fetched directly as before.
 */
const KEY_PARTS = new RegExp("/(?:csa|crsi)/([0-9]+)/([^/]+?)(?:_t)?[.]jpg$", "i");

export function idsFrom(url) {
  const m = typeof url === "string" && url.match(KEY_PARTS);
  return m ? { auditId: m[1], localId: m[2] } : null;
}

/*
 * How many photographs are fetched at once.
 *
 * The first version used Promise.all across every URL, which at five hundred
 * findings means five hundred simultaneous requests. A browser serves six per
 * host and queues the rest, so it mostly worked — while holding five hundred
 * pending promises and their buffers. Eight is enough to saturate the six
 * connections without building that queue in our own memory.
 */
const CONCURRENCY = 8;

/*
 * A single photograph's patience.
 *
 * Without this, one request that never answers blocks the whole export
 * forever: the auditor watches a spinner and nothing ever surfaces, because
 * the fetch is designed never to throw. A slow image is worth waiting for; a
 * dead one must not cost the document.
 */
const TIMEOUT_MS = 15000;

/** Every distinct photo URL the report will print. */
function urlsIn(model) {
  const urls = new Set();
  (model.sections || []).forEach((s) => {
    const d = s.data || {};
    (d.rows || []).forEach((r) => (r.photos || []).forEach((u) => urls.add(u)));
    (d.pillars || []).forEach((p) =>
      (p.sub_pillars || []).forEach((sp) =>
        (sp.findings || []).forEach((f) => (f.photos || []).forEach((u) => urls.add(u)))));
  });
  return [...urls];
}

async function fetchOne(url, report, viaApi) {
  /*
   * Through the API first.
   *
   * Straight-to-S3 is the obvious route and the one that fails in practice:
   * the objects are public, CORS is open, and a browser extension or a
   * corporate proxy still turns it into "Failed to fetch". The API is the
   * origin the app is already talking to, so it works wherever the app does.
   *
   * S3 stays as the fallback, for a URL this cannot parse — a photograph
   * stored somewhere else entirely — rather than as the first choice.
   */
  const ids = viaApi && idsFrom(url);
  if (ids) {
    try {
      return await viaApi(ids.auditId, ids.localId);
    } catch (e) {
      report(url, "api: " + ((e && e.message) || "failed"));
      // and fall through to S3 rather than giving up on the picture
    }
  }

  const ctrl = typeof AbortController === "function" ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), TIMEOUT_MS) : null;
  const asked = thumbFor(url);
  try {
    const res = await fetch(asked, ctrl ? { signal: ctrl.signal } : undefined);
    if (!res.ok) {
      report(asked, "HTTP " + res.status);
      return null;
    }
    return new Uint8Array(await res.arrayBuffer());
  } catch (e) {
    /*
     * Recorded rather than only swallowed.
     *
     * Swallowing kept the export alive, which is right, and made a failure
     * invisible, which is not: a picture that never arrives looks exactly like
     * a picture nobody took. A blocked request, a proxy, an extension and a
     * dead network all produce the same empty cell, and the only way to tell
     * them apart is to say what happened.
     */
    report(asked, (e && e.name === "AbortError")
      ? "timed out after " + TIMEOUT_MS + "ms"
      : (e && e.message) || "fetch failed");
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Fetch every photograph the report will print, as bytes.
 *
 * `docx` and `exceljs` embed images from data, not from a URL, so they have to
 * be in hand before the document is built. react-pdf fetches its own, which is
 * why Word was the format that ended up printing "1 photo" and nothing else.
 *
 * Thumbnails, not originals — the same 178 MB-against-28 MB choice (§7a).
 *
 * **Never throws, and never hangs.** A photograph that will not load must not
 * cost an auditor their whole export: it is simply absent from the map, and
 * each exporter falls back to naming the count or printing the URL. The
 * returned map carries a `missing` count so a caller can say so out loud
 * rather than leaving a reader to notice the gap themselves.
 */
export async function fetchPhotos(model, viaApi) {
  const urls = urlsIn(model);
  const out = new Map();
  let missing = 0;

  /*
   * A fixed pool rather than Promise.all: each worker takes the next URL when
   * it finishes one, so at most CONCURRENCY requests and buffers exist at any
   * moment regardless of how many photographs the report holds.
   */
  const failures = [];
  const report = (asked, why) => {
    failures.push({ url: asked, why });
    // First one only: five hundred identical lines help nobody.
    if (failures.length === 1) {
      console.warn("CSA photo fetch failed: " + why, asked);
    }
  };

  let next = 0;
  const worker = async () => {
    while (next < urls.length) {
      const u = urls[next++];
      const bytes = await fetchOne(u, report, viaApi);
      if (bytes) out.set(u, bytes);
      else missing++;
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(CONCURRENCY, urls.length) }, worker)
  );

  out.missing = missing;
  out.requested = urls.length;
  out.failures = failures;
  return out;
}

export const PHOTO_FETCH = { CONCURRENCY, TIMEOUT_MS };

/**
 * What happened to the photographs, in a sentence an auditor can act on.
 *
 * Returns null when there was nothing to fetch or nothing went wrong — there
 * is no point telling somebody their report is fine. It speaks up only when a
 * picture is missing, because that is the case where the document looks
 * complete and is not.
 */
export function photoReport(images) {
  if (!images || !images.requested) return null;
  if (!images.missing) return null;
  const why = (images.failures && images.failures[0] && images.failures[0].why) || "unknown";
  return images.missing + " of " + images.requested +
    " photo" + (images.requested === 1 ? "" : "s") +
    " could not be loaded (" + why + ")";
}

/** Bytes as a data URI, which is a source every renderer can load. */
function dataUriFor(bytes) {
  let bin = "";
  const chunk = 0x8000;                 // avoids blowing the argument limit
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return "data:image/jpeg;base64," + btoa(bin);
}

/**
 * A copy of the model with every photograph inlined.
 *
 * react-pdf fetches image sources itself and cannot send an Authorization
 * header, so it cannot be pointed at the API route the other two exporters
 * use — and pointed at S3 it fails the same way they did. Handing it a data
 * URI sidesteps both: the bytes are already in hand by the time the renderer
 * runs.
 *
 * The model is copied rather than edited. The caller's model is the one on
 * screen, and rewriting it would leave the preview holding megabytes of
 * base64 long after the document was made.
 */
export async function inlinePhotos(model, viaApi) {
  const images = await fetchPhotos(model, viaApi);
  if (!images.size) return { model, images };

  const uris = new Map();
  images.forEach((bytes, url) => uris.set(url, dataUriFor(bytes)));

  const swap = (list) => (list || []).map((u) => uris.get(u) || u);
  const copy = JSON.parse(JSON.stringify(model));
  (copy.sections || []).forEach((s) => {
    const d = s.data || {};
    (d.rows || []).forEach((r) => { r.photos = swap(r.photos); });
    (d.pillars || []).forEach((pil) =>
      (pil.sub_pillars || []).forEach((sp) =>
        (sp.findings || []).forEach((f) => { f.photos = swap(f.photos); })));
  });

  return { model: copy, images };
}

/**
 * A bundled asset's bytes — the cover artwork, the wordmark.
 *
 * Same-origin, so none of the trouble S3 caused applies; webpack turns the
 * import into a path under /static/media and the app is already allowed to
 * fetch from itself.
 *
 * `docx` and `exceljs` embed data rather than URLs, so an asset they are to
 * draw has to be fetched first. react-pdf does its own, which is why only
 * these two need it.
 *
 * Returns null rather than throwing: a missing logo should cost a logo, not a
 * report.
 */
export async function assetBytes(url) {
  if (!url) return null;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return new Uint8Array(await res.arrayBuffer());
  } catch (e) {
    return null;
  }
}
